import { SpaceProvider, useSpaces } from "./Spaces";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, clearPrefetch, prefetch, setCsrfToken } from "./api";
import { disablePushForThisDevice } from "./push";
import { SaveQueue } from "./SaveQueue";
import { WaitingPoolView, waitingPoolReads, type HomeScreen } from "./WaitingPoolView";
import { ResultScopeEditor } from "./PairingExperience";
import { ParticipantFrame, PossibilityArt, AppIcon, LoadingState, clearScrollMemory } from "./ParticipantShell";
import { RevisionConflictNotice } from "./RevisionConflictNotice";
import { applyQuestionnaireCopy, submissionConfirmationLabel } from "./questionnaire-copy";
import { apiErrorCopy } from "../shared/api-error-copy";
import { QuestionnaireBoundaryStep, PreviousQuestionnaireAnswers, boundaryAnswers, BOUNDARY_LABELS } from "./QuestionnaireBoundaryStep";
import "./romantic-answers.css";
import {
  DeliberateActionAttempt,
  isRevisionConflictCode,
  waitingPoolErrorMessage,
} from "../shared/waiting-pool-client";
import type { ParticipationPayload } from "./waiting-pool-types";
import type {
  AnswerValue,
  DraftAnswerValue,
  ClientField,
  ClientUnit,
  DraftDocument,
  QuestionnaireDefinition,
  Snapshot,
  ValidationIssue,
  CountrySearchResult,
  InboundLocationMode,
  LocationSearchResult,
  LocationSettingsPayload,
} from "./types";

type CoreView = "questionnaire" | "snapshot" | "locked" | "participation";
type View = CoreView | "review" | "location" | "account";
type SaveStatus = "idle" | "saving" | "saved" | "error" | "conflict";

interface SessionPayload { accountId: string; csrfToken: string; expiresAt: string }
interface SessionProbe extends Partial<SessionPayload> { authenticated: boolean }
interface DraftPayload {
  status: "draft" | "submitted";
  document?: DraftDocument;
  revision?: number;
  currentUnitId?: string | null;
  updatedAt?: string | null;
}

const CURRENT_QUESTIONNAIRE_VERSION = "1.2.0";

const EMPTY_DRAFT: DraftDocument = {
  schemaVersion: "1.2.0",
  context: {},
  answers: {},
  evidenceSources: {},
  otherTexts: {},
  f01Reviewed: true,
};

function scalarAnswer(value: DraftAnswerValue | undefined): AnswerValue | undefined {
  if (typeof value === "string" || typeof value === "number") return value;
  if (Array.isArray(value) && value.every((item) => typeof item === "string")) return [...value] as string[];
  return undefined;
}

function useAutosave(
  document: DraftDocument,
  currentUnitId: string | null,
  initialRevision: number,
  enabled: boolean,
) {
  const [revision, setRevision] = useState(initialRevision);
  const revisionRef = useRef(initialRevision);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [message, setMessage] = useState("");
  const lastSaved = useRef(initialRevision === 0 ? "" : JSON.stringify({ document, currentUnitId }));
  const queue = useRef(new SaveQueue());

  const persist = useCallback((): Promise<number> => {
    const signature = JSON.stringify({ document, currentUnitId });
    return queue.current.run(async () => {
      if (!enabled || signature === lastSaved.current) return revisionRef.current;
      setStatus("saving"); setMessage("Saving");
      try {
        const result = await api<{revision:number;updatedAt:string}>(`/api/v1/questionnaire/draft?version=${document.schemaVersion}`, {
          method:"PUT",body:JSON.stringify({revision:revisionRef.current,currentUnitId,document}),
        });
        revisionRef.current=result.revision;setRevision(result.revision);lastSaved.current=signature;
        setStatus("saved");setMessage("Saved");return result.revision;
      } catch(error) {
        if(error instanceof ApiError && error.code === "draft_conflict") {setStatus("conflict");setMessage("草稿在另一处发生了变化，请刷新页面");}
        else {setStatus("error");setMessage(error instanceof Error ? error.message : "Save failed");}
        throw error;
      }
    });
  }, [currentUnitId,document,enabled]);

  useEffect(() => {
    if (!enabled) return;
    const signature = JSON.stringify({ document, currentUnitId });
    if (signature === lastSaved.current) return;
    const timer = window.setTimeout(() => { void persist().catch(() => undefined); }, 700);
    return () => window.clearTimeout(timer);
  }, [currentUnitId, document, enabled, persist]);

  const hasUnsaved = JSON.stringify({document,currentUnitId}) !== lastSaved.current;
  return { revision, status, message: hasUnsaved && (status === "idle" || status === "saved") ? "Not saved" : message, persist, hasUnsaved };
}

function AuthScreen({ onAuthenticated, notice = "" }: { onAuthenticated: (session: SessionPayload, created?: boolean) => void; notice?: string }) {
  const [welcome, setWelcome] = useState(!notice);
  const [mode, setMode] = useState<"create" | "login" | "recover">(notice ? "login" : "create");
  const [accountId, setAccountId] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryKey, setRecoveryKey] = useState("");
  const [created, setCreated] = useState<(SessionPayload & { recoveryKey: string }) | null>(null);
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(notice);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "create") {
        const result = await api<SessionPayload & { recoveryKey: string }>("/api/v1/accounts", {
          method: "POST",
          body: JSON.stringify({ inviteCode, password }),
        });
        setCsrfToken(result.csrfToken);
        setCreated(result);
      } else if (mode === "login") {
        const result = await api<SessionPayload>("/api/v1/sessions", {
          method: "POST",
          body: JSON.stringify({ accountId, password }),
        });
        setCsrfToken(result.csrfToken);
        onAuthenticated(result);
      } else {
        const result = await api<SessionPayload & { recoveryKey: string }>("/api/v1/recovery", {
          method: "POST",
          body: JSON.stringify({ accountId, recoveryKey, newPassword: password }),
        });
        setCsrfToken(result.csrfToken);
        setCreated(result);
      }
    } catch (caught) {
      setError(caught instanceof ApiError
        ? ({
          registration_closed: "注册暂未开放。已有账户仍可登录。",
          maintenance_in_progress: "服务正在维护，当前内容尚未提交。请稍后再试。",
          production_safety_configuration_invalid: "服务暂时无法使用，请稍后再试。",
        }[caught.code] ?? caught.message)
        : caught instanceof Error ? caught.message : "请求未能完成");
    } finally {
      setBusy(false);
    }
  };

  if (created) return <ParticipantFrame title="Account Details" screenKey="credentials" balanced>
    <main className="credential-sheet">
      <h2>保存登录信息</h2>
      <p>忘记密码时，需要账户编号和恢复密钥找回账户。这里不支持邮箱找回。</p>
      <dl><div><dt>账户编号</dt><dd>{created.accountId}</dd></div><div><dt>恢复密钥</dt><dd>{created.recoveryKey}</dd></div></dl>
      <button type="button" className="text-button" onClick={async()=>{try{await navigator.clipboard.writeText(`账户编号：${created.accountId}\n恢复密钥：${created.recoveryKey}`);setCopied(true);setError("");}catch{setError("暂时无法复制，请选中文字并保存。");}}}>{copied?"Copied":"Copy account details"}</button>
      {error&&<p role="alert" className="error-message">{error}</p>}
      <p className="field-help">恢复密钥离开本页后不会再次显示。请存入密码管理器。</p>
      <label className="confirmation-row"><input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} /><span>我已经把账户编号和恢复密钥保存在安全的位置</span></label>
      <button className="button primary" disabled={!acknowledged} onClick={() => onAuthenticated(created, mode === "create")}>Continue</button>
    </main>
  </ParticipantFrame>;
  if (welcome) return <ParticipantFrame screenKey="welcome" balanced>
    <main className="r4-welcome"><PossibilityArt /><h1>在关系<br />发生之前</h1>
      <div className="r4-choice-actions"><button className="button" onClick={() => {setMode("create");setWelcome(false);}}>我有邀请码</button><button className="button" onClick={() => {setMode("login");setWelcome(false);}}>已有账户，登录</button></div>
    </main>
  </ParticipantFrame>;
  return <ParticipantFrame title={mode === "create" ? "Invitation" : mode === "login" ? "Log In" : "Account Recovery"} screenKey={mode} onBack={() => {setError("");setWelcome(true);}}>
    <main className="r4-auth">
      <form onSubmit={submit} className="auth-form">
        {mode === "create" && <input className="visually-hidden" aria-hidden="true" tabIndex={-1} autoComplete="username" value="new-anonymous-account" readOnly />}
        {mode === "create" && <label><span>邀请码</span><input value={inviteCode} onChange={(event) => setInviteCode(event.target.value)} autoComplete="off" placeholder="填写邀请码" required /></label>}
        {mode !== "create" && <label><span>账户编号</span><input value={accountId} onChange={(event) => setAccountId(event.target.value)} autoComplete="username" required /></label>}
        {mode === "recover" && <label><span>恢复密钥</span><input value={recoveryKey} onChange={(event) => setRecoveryKey(event.target.value)} autoComplete="off" required /></label>}
        <label><span>{mode === "recover" ? "新密码" : "密码"}</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder={mode !== "login" ? "至少 12 个字符" : ""} minLength={12} maxLength={128} required /></label>
        {mode === "create" && <section className="r4-auth-privacy"><h2>关于回答和隐私</h2><p>问卷回答仅自己可见。选择题按固定规则用于匹配；开放题只保存原文，不交给 AI 分析。</p><details><summary>账户与数据</summary><p>提交前可以修改，提交后保留为不可修改的记录。加入匹配后，可能会看到一个可以继续了解的人，再决定是否愿意认识。对方只能看到你确认展示的资料，无法查看你的问卷、私密偏好或内部评估。联系方式可选。登录后可以永久删除账户及其关联数据。</p></details></section>}
        {error && <p className="error-message" role="alert">{error}</p>}
        <button className="button primary" disabled={busy}>{busy ? "请稍候" : mode === "create" ? "建立匿名账户" : mode === "login" ? "Log in" : "重设密码"}</button>
      </form>
      {mode === "login" && <button className="text-button r4-forgot" onClick={() => {setError("");setMode("recover");}}>忘记密码</button>}
    </main>
  </ParticipantFrame>;
}

function fieldIssues(issues: ValidationIssue[], path: string): ValidationIssue[] {
  return issues.filter((issue) => issue.path === path);
}

function uniqueLocationParts(parts: Array<string | null | undefined>): string {
  const seen = new Set<string>();
  return parts.filter((part): part is string => {
    if (!part) return false;
    const key = part.normalize("NFKC").toLocaleLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).join(" · ");
}

/** One Location page, three independent settings: where I am, who can see me, whom I want to meet. Each saves on its own. */
function LocationSettings({ onBack, onLogout, focus = null }: { onBack: () => void; onLogout: () => void; focus?: "preferred" | null }) {
  const [settings, setSettings] = useState<LocationSettingsPayload | null>(null);
  const [participation, setParticipation] = useState<ParticipationPayload | null>(null);
  const [countryQuery, setCountryQuery] = useState("");
  const [countries, setCountries] = useState<CountrySearchResult[]>([]);
  const [countryOpen, setCountryOpen] = useState(false);
  const [country, setCountry] = useState<CountrySearchResult | null>(null);
  const [cityQuery, setCityQuery] = useState("");
  const [cities, setCities] = useState<LocationSearchResult[]>([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [city, setCity] = useState<LocationSearchResult | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualLabel, setManualLabel] = useState("");
  const [mode, setMode] = useState<InboundLocationMode>("same_city");
  const [selectedCountries, setSelectedCountries] = useState<CountrySearchResult[]>([]);
  const [selectedPlaces, setSelectedPlaces] = useState<LocationSearchResult[]>([]);
  const [scopeCountryQuery, setScopeCountryQuery] = useState("");
  const [scopeCountryOptions, setScopeCountryOptions] = useState<CountrySearchResult[]>([]);
  const [scopeCountryOpen, setScopeCountryOpen] = useState(false);
  const [scopeCountry, setScopeCountry] = useState<CountrySearchResult | null>(null);
  const [scopeCityQuery, setScopeCityQuery] = useState("");
  const [scopeCityOptions, setScopeCityOptions] = useState<LocationSearchResult[]>([]);
  const [scopeCityOpen, setScopeCityOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [revisionConflict, setRevisionConflict] = useState(false);
  const [activeOutcome, setActiveOutcome] = useState<"save_and_pause" | "continue_with_boundary" | null>(null);
  const locationAttempt = useRef(new DeliberateActionAttempt());
  const locationConflictRef = useRef<HTMLDivElement>(null);
  const loadGeneration = useRef(0);

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    const [result, participationResult] = await Promise.all([
      api<LocationSettingsPayload>("/api/v1/location-settings?locale=zh-CN"),
      api<ParticipationPayload>("/api/v1/participation?locale=zh-CN"),
    ]);
    if (generation !== loadGeneration.current) return;
    setSettings(result);
    setParticipation(participationResult);
    setMode(result.boundary.mode);
    setSelectedCountries(result.boundarySelection.countries);
    setSelectedPlaces(result.boundarySelection.places);
    if (result.location.countryRegionCode && result.display) {
      setCountry({ countryRegionCode: result.location.countryRegionCode, displayName: result.display.countryDisplayName });
    }
    if (result.location.placeId && result.display?.displayName) {
      setCity({
        placeId: result.location.placeId,
        countryRegionCode: result.location.countryRegionCode!,
        displayName: result.display.displayName,
        admin1DisplayName: result.display.admin1DisplayName ?? null,
        countryDisplayName: result.display.countryDisplayName,
      });
    }
    setManualMode(result.location.resolutionStatus === "pending");
    setManualLabel(result.location.manualLocationLabel ?? "");
  }, []);

  useEffect(() => {
    void load().then(() => setLoadError(""))
      .catch((caught) => setLoadError(caught instanceof Error ? caught.message : "无法读取地点设置"));
  }, [load]);

  useEffect(() => {
    if (revisionConflict) locationConflictRef.current?.focus();
  }, [revisionConflict]);

  const handleLocationFailure = async (caught: unknown, fallback: string) => {
    if (caught instanceof ApiError && isRevisionConflictCode(caught.code)) {
      await load();
      setRevisionConflict(true);
      return;
    }
    setActionError(caught instanceof ApiError
      ? waitingPoolErrorMessage(caught.code, caught.message)
      : caught instanceof Error ? caught.message : fallback);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void api<{ results: CountrySearchResult[] }>(`/api/v1/locations/countries?locale=zh-CN&q=${encodeURIComponent(countryQuery)}`)
        .then((result) => setCountries(result.results))
        .catch(() => setCountries([]));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [countryQuery]);

  useEffect(() => {
    if (!country || cityQuery.trim().length < 1) {
      setCities([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void api<{ results: LocationSearchResult[] }>(`/api/v1/locations/search?locale=zh-CN&countryRegionCode=${country.countryRegionCode}&q=${encodeURIComponent(cityQuery)}`)
        .then((result) => setCities(result.results))
        .catch(() => setCities([]));
    }, 220);
    return () => window.clearTimeout(timer);
  }, [cityQuery, country]);

  useEffect(() => {
    if (mode !== "selected_locations") return;
    const timer = window.setTimeout(() => {
      void api<{ results: CountrySearchResult[] }>(`/api/v1/locations/countries?locale=zh-CN&q=${encodeURIComponent(scopeCountryQuery)}`)
        .then((result) => setScopeCountryOptions(result.results))
        .catch(() => setScopeCountryOptions([]));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [mode, scopeCountryQuery]);

  useEffect(() => {
    if (mode !== "selected_locations" || !scopeCountry || scopeCityQuery.trim().length < 1) {
      setScopeCityOptions([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void api<{ results: LocationSearchResult[] }>(`/api/v1/locations/search?locale=zh-CN&countryRegionCode=${scopeCountry.countryRegionCode}&q=${encodeURIComponent(scopeCityQuery)}`)
        .then((result) => setScopeCityOptions(result.results))
        .catch(() => setScopeCityOptions([]));
    }, 220);
    return () => window.clearTimeout(timer);
  }, [mode, scopeCityQuery, scopeCountry]);

  const saveCurrent = async () => {
    if (!settings) return;
    if (!country) {
      setActionError("请先选择国家或地区");
      return;
    }
    if (!manualMode && !city) {
      setActionError("请选择城市；找不到时，也可以填写所在地名称。");
      return;
    }
    setBusy(true);
    setActionError("");
    setMessage("");
    try {
      const requiresActiveChoice = participation?.participationStatus === "active" && manualMode;
      if (requiresActiveChoice && !activeOutcome) {
        setActionError("请决定先暂停匹配，还是扩大地点范围后继续匹配。");
        return;
      }
      if (requiresActiveChoice && activeOutcome === "continue_with_boundary" && (
        mode === "same_city"
        || !settings.preferenceRevision
        || (mode === "selected_locations" && selectedCountries.length === 0 && selectedPlaces.length === 0)
      )) {
        setActionError("继续匹配前，请选择同一国家或地区、自选地点或全球范围。");
        return;
      }
      const body = {
        location: manualMode
          ? { countryRegionCode: country.countryRegionCode, placeId: null, resolutionStatus: "pending", manualLocationLabel: manualLabel }
          : { countryRegionCode: country.countryRegionCode, placeId: city!.placeId, resolutionStatus: "resolved", manualLocationLabel: null },
        expectedLocationRevision: settings.locationRevision,
        ...(requiresActiveChoice ? {
          activeOutcome,
          ...(activeOutcome === "continue_with_boundary" ? {
            expectedPreferenceRevision: settings.preferenceRevision,
            boundary: {
              mode,
              countryRegionCodes: mode === "selected_locations" ? selectedCountries.map((item) => item.countryRegionCode) : [],
              placeIds: mode === "selected_locations" ? selectedPlaces.map((item) => item.placeId) : [],
            },
          } : {}),
        } : {}),
      };
      const response = await api<unknown>("/api/v1/location-settings/current?locale=zh-CN", {
        method: "PUT",
        headers: requiresActiveChoice
          ? { "Idempotency-Key": locationAttempt.current.keyFor(body) }
          : undefined,
        body: JSON.stringify(body),
      });
      void response;
      if (requiresActiveChoice) locationAttempt.current.settle();
      setRevisionConflict(false);
      await load();
      setActiveOutcome(null);
      setMessage(requiresActiveChoice && activeOutcome === "save_and_pause"
        ? "所在地已提交为待确认，匹配参与已暂停。"
        : manualMode ? "所在地已提交为待确认。" : "当前所在地已保存。");
    } catch (caught) {
      await handleLocationFailure(caught, "所在地未能保存");
    } finally {
      setBusy(false);
    }
  };

  const saveBoundary = async () => {
    if (!settings) return;
    setBusy(true);
    setActionError("");
    setMessage("");
    try {
      const result = await api<LocationSettingsPayload>("/api/v1/location-settings/boundary?locale=zh-CN", {
        method: "PUT",
        body: JSON.stringify({
          expectedRevision: settings.preferenceRevision,
          boundary: {
            mode,
            countryRegionCodes: mode === "selected_locations" ? selectedCountries.map((item) => item.countryRegionCode) : [],
            placeIds: mode === "selected_locations" ? selectedPlaces.map((item) => item.placeId) : [],
          },
        }),
      });
      setSettings(result);
      setSelectedCountries(result.boundarySelection.countries);
      setSelectedPlaces(result.boundarySelection.places);
      setRevisionConflict(false);
      setMessage("地点范围已保存。");
    } catch (caught) {
      await handleLocationFailure(caught, "地点范围未能保存");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <main className="center-state">{loadError ? <p role="alert">{loadError}</p> : <LoadingState />}</main>;
  const pending = settings.location.resolutionStatus === "pending";
  const resolved = settings.location.resolutionStatus === "resolved";
  const draftBoundaryReady = Boolean(settings.location.countryRegionCode)
    && (mode !== "same_city" || resolved)
    && (mode !== "selected_locations" || selectedCountries.length > 0 || selectedPlaces.length > 0);
  return <div className="settings-layout">
    <header className="app-header">
      <button className="wordmark" onClick={onBack}>Perhaps</button>
      <span className="save-status">地点设置</span>
      <button className="text-button" onClick={onLogout}>退出</button>
    </header>
    <main className="settings-main">
      <header className="reading-header">

        <h1>我的地点设置</h1>
        <p>这里有三项设置：所在地、谁可以看到你、想认识哪里的人。三项分别保存，彼此不会自动改变。地点只用来筛选范围，不影响匹配评分。</p>
      </header>

      <section className="settings-section" aria-labelledby="current-location-heading">
        <div className="settings-heading">
          <h2 id="current-location-heading">当前所在地</h2>
          {resolved && settings.display && <p className="location-summary">{uniqueLocationParts([settings.display.displayName, settings.display.admin1DisplayName, settings.display.countryDisplayName])}</p>}
          {pending && <p className="location-summary">待确认 · {settings.location.manualLocationLabel}</p>}
        </div>
        {revisionConflict && <RevisionConflictNotice
          section="location"
          ref={locationConflictRef}
          onDismiss={() => setRevisionConflict(false)}
        />}

        <div className="combobox-field">
          <label htmlFor="country-search">国家或地区</label>
          <input
            id="country-search"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={countryOpen && countries.length > 0}
            aria-controls="country-options"
            value={countryQuery}
            placeholder={country?.displayName ?? "输入名称搜索"}
            onFocus={() => setCountryOpen(true)}
            onChange={(event) => { setCountryQuery(event.target.value); setCountryOpen(true); setCountry(null); setCity(null); setCityQuery(""); }}
          />
          {countryOpen && countries.length > 0 && <ul className="combobox-options" id="country-options" role="listbox">
            {countries.map((item) => <li key={item.countryRegionCode}>
              <button role="option" aria-selected={country?.countryRegionCode === item.countryRegionCode} onClick={() => {
                setCountry(item); setCountryQuery(item.displayName); setCountryOpen(false); setCity(null); setCityQuery(""); setManualMode(false);
              }}>{item.displayName}<span>{item.countryRegionCode}</span></button>
            </li>)}
          </ul>}
        </div>

        {country && !manualMode && <div className="combobox-field">
          <label htmlFor="city-search">城市</label>
          <input
            id="city-search"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={cityOpen && cities.length > 0}
            aria-controls="city-options"
            value={cityQuery}
            placeholder={city?.displayName ?? "输入城市名称"}
            onFocus={() => setCityOpen(true)}
            onChange={(event) => { setCityQuery(event.target.value); setCityOpen(true); setCity(null); }}
          />
          {cityOpen && cities.length > 0 && <ul className="combobox-options" id="city-options" role="listbox">
            {cities.map((item) => <li key={item.placeId}>
              <button role="option" aria-selected={city?.placeId === item.placeId} onClick={() => {
                setCity(item); setCityQuery(item.displayName); setCityOpen(false);
              }}><strong>{item.displayName}</strong><span>{uniqueLocationParts([item.admin1DisplayName, item.countryDisplayName])}</span></button>
            </li>)}
          </ul>}
          <button className="text-button inline-action" onClick={() => { loadGeneration.current += 1; setManualMode(true); setCity(null); setCityQuery(""); }}>找不到城市？</button>
        </div>}

        {country && manualMode && <div className="combobox-field">
          <label htmlFor="manual-location">所在地名称</label>
          <input id="manual-location" value={manualLabel} maxLength={120} onChange={(event) => setManualLabel(event.target.value)} placeholder="例如：某某市或附近地区" />
          <p className="field-help">确认所在地之前，暂时无法使用同城匹配。</p>
          <button className="text-button inline-action" onClick={() => { loadGeneration.current += 1; setManualMode(false); }}>返回城市搜索</button>
        </div>}

        {participation?.participationStatus === "active" && manualMode && <fieldset className="active-location-choice">
          <legend>等待地点确认期间，要继续匹配吗？</legend>
          <p className="field-help">继续匹配需要扩大地点范围，由你决定是否调整。</p>
          <label className="option-row">
            <input type="radio" name="active-location-outcome" checked={activeOutcome === "save_and_pause"}
              onChange={() => setActiveOutcome("save_and_pause")} />
            <span><strong>保存并暂停参与</strong><small>等待地点确认后再决定何时恢复。</small></span>
          </label>
          <label className="option-row">
            <input type="radio" name="active-location-outcome" checked={activeOutcome === "continue_with_boundary"}
              onChange={() => setActiveOutcome("continue_with_boundary")} />
            <span><strong>扩大范围，继续匹配</strong><small>请在下方选择同一国家或地区、自选地点或全球。</small></span>
          </label>
        </fieldset>}

        <div className="settings-actions"><button className="button primary" disabled={busy || !country || (!manualMode && !city) || (manualMode && !manualLabel.trim())} onClick={() => void saveCurrent()}>{busy ? "Saving" : "保存当前所在地"}</button></div>
      </section>

      <section className="settings-section" aria-labelledby="boundary-heading">
        <h2 id="boundary-heading">哪些地方的人可以看到我</h2>
        <p className="section-intro">这里决定哪些地方的人可能看到你。想看哪些地方的人，在 Preferred Locations 里单独设置。范围不会被自动扩大。</p>
        <div className="option-list boundary-options">
          {([
            ["same_city", "同城", "仅在双方城市都已确认时可用。"],
            ["same_country_region", "同一国家或地区", "所在地待确认时也可选择。"],
            ["selected_locations", "自选地点", "明确加入一个或多个国家、地区或城市。"],
            ["global", "全球", "世界各地的人符合彼此的匹配条件时，都可能看到你。"],
          ] as const).map(([value, label, help]) => <label className="option-row" key={value}>
            <input type="radio" name="inbound-location-mode" value={value} checked={mode === value} disabled={value === "same_city" && !resolved} onChange={() => setMode(value)} />
            <span><strong>{label}</strong><small>{help}</small></span>
          </label>)}
        </div>
        {mode === "selected_locations" && <section className="scope-picker" aria-labelledby="scope-picker-heading">
          <h3 id="scope-picker-heading">自选范围</h3>
          {(selectedCountries.length > 0 || selectedPlaces.length > 0) && <div className="location-tokens" aria-label="已选择地点">
            {selectedCountries.map((item) => <span key={item.countryRegionCode}>{item.displayName}<button aria-label={`移除 ${item.displayName}`} onClick={() => setSelectedCountries((current) => current.filter((entry) => entry.countryRegionCode !== item.countryRegionCode))}>移除</button></span>)}
            {selectedPlaces.map((item) => <span key={item.placeId}>{uniqueLocationParts([item.displayName, item.admin1DisplayName, item.countryDisplayName])}<button aria-label={`移除 ${item.displayName}`} onClick={() => setSelectedPlaces((current) => current.filter((entry) => entry.placeId !== item.placeId))}>移除</button></span>)}
          </div>}
          <div className="combobox-field">
            <label htmlFor="scope-country-search">添加国家、地区或其中的城市</label>
            <input
              id="scope-country-search"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={scopeCountryOpen && scopeCountryOptions.length > 0}
              aria-controls="scope-country-options"
              value={scopeCountryQuery}
              placeholder={scopeCountry?.displayName ?? "先选择国家或地区"}
              onFocus={() => setScopeCountryOpen(true)}
              onChange={(event) => { setScopeCountryQuery(event.target.value); setScopeCountryOpen(true); setScopeCountry(null); setScopeCityQuery(""); }}
            />
            {scopeCountryOpen && scopeCountryOptions.length > 0 && <ul className="combobox-options" id="scope-country-options" role="listbox">
              {scopeCountryOptions.map((item) => <li key={item.countryRegionCode}><button role="option" aria-selected={scopeCountry?.countryRegionCode === item.countryRegionCode} onClick={() => {
                setScopeCountry(item); setScopeCountryQuery(item.displayName); setScopeCountryOpen(false); setScopeCityQuery("");
              }}>{item.displayName}<span>{item.countryRegionCode}</span></button></li>)}
            </ul>}
          </div>
          {scopeCountry && <div className="scope-country-actions">
            <button className="button secondary" disabled={selectedCountries.length >= 25 || selectedCountries.some((item) => item.countryRegionCode === scopeCountry.countryRegionCode)} onClick={() => setSelectedCountries((current) => [...current, scopeCountry])}>加入整个国家或地区</button>
            <div className="combobox-field">
              <label htmlFor="scope-city-search">或添加其中的城市</label>
              <input
                id="scope-city-search"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={scopeCityOpen && scopeCityOptions.length > 0}
                aria-controls="scope-city-options"
                value={scopeCityQuery}
                placeholder="搜索城市"
                onFocus={() => setScopeCityOpen(true)}
                onChange={(event) => { setScopeCityQuery(event.target.value); setScopeCityOpen(true); }}
              />
              {scopeCityOpen && scopeCityOptions.length > 0 && <ul className="combobox-options" id="scope-city-options" role="listbox">
                {scopeCityOptions.map((item) => <li key={item.placeId}><button role="option" disabled={selectedPlaces.length >= 50 && !selectedPlaces.some((entry) => entry.placeId === item.placeId)} aria-selected={selectedPlaces.some((entry) => entry.placeId === item.placeId)} onClick={() => {
                  if (selectedPlaces.length < 50 && !selectedPlaces.some((entry) => entry.placeId === item.placeId)) setSelectedPlaces((current) => [...current, item]);
                  setScopeCityQuery(""); setScopeCityOpen(false);
                }}><strong>{item.displayName}</strong><span>{uniqueLocationParts([item.admin1DisplayName, item.countryDisplayName])}</span></button></li>)}
              </ul>}
            </div>
          </div>}
          {selectedCountries.length === 0 && selectedPlaces.length === 0 && <p className="field-help">至少加入一个国家、地区或城市。系统不会自动添加你的当前所在地。</p>}
        </section>}
        {pending && <div className="pending-note"><strong>你可以先等待地点确认。</strong><p>这段时间仍可以完成问卷和资料，只是暂时不会加入 Matching Pool。愿意扩大地点范围的话，设置之后就可以继续匹配。</p></div>}
        <div className="settings-actions">
          <button className="button primary" disabled={busy || !settings.location.countryRegionCode || (mode === "same_city" && !resolved) || (mode === "selected_locations" && selectedCountries.length === 0 && selectedPlaces.length === 0)} onClick={() => void saveBoundary()}>{busy ? "Saving" : "保存地点范围"}</button>
        </div>
        <p className={`eligibility ${draftBoundaryReady ? "eligible" : "waiting"}`} aria-live="polite">
          {draftBoundaryReady ? "当前选择满足加入 Matching Pool 的地点条件。" : "地点设置尚未满足匹配条件。"}
        </p>
      </section>
      {participation?.participationStatus !== "ended" && <div className="settings-section r4-preferred-section"><ResultScopeEditor onSaved={async () => true} focusOnLoad={focus === "preferred"} /></div>}
      {message && <p className="success-message" role="status">{message}</p>}
      {loadError && <p className="error-message" role="alert">{loadError}</p>}
      {actionError && <p className="error-message" role="alert">{actionError}</p>}
    </main>
  </div>;
}

function FieldInput({
  unitId,
  field,
  value,
  issues,
  onChange,
  otherText,
  onOtherTextChange,
}: {
  unitId: string;
  field: ClientField;
  value: AnswerValue | undefined;
  issues: ValidationIssue[];
  onChange: (value: AnswerValue) => void;
  otherText?: string;
  onOtherTextChange?: (value: string) => void;
}) {
  const path = `answers.${unitId}.${field.id}`;
  const errors = [...fieldIssues(issues, path), ...fieldIssues(issues, `otherTexts.${field.id}`)];
  const optional = !field.required || (field.type === "multi" && field.minSelections === 0);
  const labelId = `${unitId}-${field.id}-label`;
  return <fieldset className="question-field" aria-describedby={field.helpText || errors.length > 0 ? `${unitId}-${field.id}-help` : undefined}>
    <legend id={labelId}>{field.prompt}{optional && <span className="required-mark"> 可不填</span>}</legend>
    {field.note && <p className="field-note">{field.note}</p>}
    {field.type === "single" && <div className="option-list">
      {field.options?.map((option) => <label className="option-row" key={option.id}>
        <input type="radio" name={`${unitId}.${field.id}`} checked={value === option.id} onChange={() => onChange(option.id)} />
        <span>{option.label}</span>
      </label>)}
    </div>}
    {field.type === "multi" && <div className="option-list">
      {field.options?.map((option) => {
        const selected = Array.isArray(value) ? value : [];
        const checked = selected.includes(option.id);
        const atMax = field.maxSelections !== undefined && selected.length >= field.maxSelections;
        return <label className="option-row" key={option.id}>
          <input type="checkbox" checked={checked} disabled={!checked && atMax} onChange={() => onChange(checked ? selected.filter((id) => id !== option.id) : [...selected, option.id])} />
          <span>{option.label}</span>
        </label>;
      })}
      {field.maxSelections !== undefined && !field.prompt.includes("最多") && <p className="field-help">最多选 {field.maxSelections} 项</p>}
    </div>}
    {field.type === "scale" && <div className="scale-wrap">
      <div className="scale-labels"><span>{field.leftLabel}</span><span>{field.rightLabel}</span></div>
      <div className="scale-options">
        {Array.from({ length: (field.max ?? 5) - (field.min ?? 1) + 1 }, (_, index) => (field.min ?? 1) + index).map((number) => <label key={number}>
          <input type="radio" name={`${unitId}.${field.id}`} checked={value === number} onChange={() => onChange(number)} />
          <span>{number}</span>
        </label>)}
      </div>
    </div>}
    {field.type === "long_text" && <div>
      <textarea aria-labelledby={labelId} value={typeof value === "string" ? value : ""} onChange={(event) => onChange(event.target.value)} maxLength={field.maxChars} rows={7} />
      <p className="field-help">{typeof value === "string" ? value.length : 0} / {field.maxChars}</p>
    </div>}
    {field.otherAllowed && (Array.isArray(value) ? value.includes("other") : value === "other") && <div>
      <label className="field-note" htmlFor={`${unitId}-${field.id}-other`}>其他想法</label>
      <textarea
        id={`${unitId}-${field.id}-other`}
        value={otherText ?? ""}
        onChange={(event) => onOtherTextChange?.(event.target.value)}
        rows={4}
      />
    </div>}
    {(field.helpText || errors.length > 0) && <div id={`${unitId}-${field.id}-help`}>
      {field.helpText && <p className="field-help">{field.helpText}</p>}
      {errors.map((issue) => <p className="error-message" role="alert" key={`${issue.path}-${issue.code}`}>{apiErrorCopy(issue.message)}</p>)}
    </div>}
  </fieldset>;
}

function ContextStep({ definition, document, onChange }: {
  definition: QuestionnaireDefinition;
  document: DraftDocument;
  onChange: (fieldId: string, value: string) => void;
}) {
  return <article className="question-sheet">
    <header className="question-header">
      <h1>{definition.context.title}</h1>
      <p>{definition.context.intro}</p>
    </header>
    {definition.context.fields.map((field) => <fieldset className="question-field" key={field.id}>
      <legend>{field.prompt}<span className="required-mark"> 必答</span></legend>
      <div className="option-list">
        {field.options.map((option) => <label className="option-row" key={option.id}>
          <input type="radio" name={`context.${field.id}`} checked={document.context[field.id] === option.id} onChange={() => onChange(field.id, option.id)} />
          <span>{option.label}</span>
        </label>)}
      </div>
    </fieldset>)}
  </article>;
}

function UnitStep({ definition, unit, document, issues, onAnswer, onEvidence, onOtherText }: {
  definition: QuestionnaireDefinition;
  unit: ClientUnit;
  document: DraftDocument;
  issues: ValidationIssue[];
  onAnswer: (fieldId: string, value: AnswerValue) => void;
  onEvidence: (value: string) => void;
  onOtherText: (fieldId: string, value: string) => void;
}) {
  return <article className="question-sheet">
    {unit.intro && <p className="field-note">{unit.intro}</p>}
    {unit.id === "F06" && document.schemaVersion === "1.2.0" && <PreviousQuestionnaireAnswers document={document} scope="F06" />}
    {unit.fields.map((field) => <FieldInput
      key={field.id}
      unitId={unit.id}
      field={field}
      value={scalarAnswer(document.answers[unit.id]?.[field.id])}
      issues={issues}
      onChange={(value) => onAnswer(field.id, value)}
      otherText={document.otherTexts?.[field.id]}
      onOtherTextChange={(value) => onOtherText(field.id, value)}
    />)}
    {unit.evidenceSourcePrompt && <fieldset className="question-field evidence-field">
      <legend>你主要依据哪类经历回答这一单元？<span className="required-mark"> 必答 · 单选</span></legend>
      <div className="option-list compact">
        {definition.evidenceSourceOptions.map((option) => <label className="option-row" key={option.id}>
          <input type="radio" name={`evidence.${unit.id}`} checked={document.evidenceSources[unit.id] === option.id} onChange={() => onEvidence(option.id)} />
          <span>{option.label}</span>
        </label>)}
      </div>
      <p className="field-help">回答依据只调整判断把握，不改变你的特征取值。</p>
      {fieldIssues(issues, `evidenceSources.${unit.id}`).map((issue) => <p className="error-message" role="alert" key={issue.code}>{apiErrorCopy(issue.message)}</p>)}
    </fieldset>}
  </article>;
}

function SnapshotView({ snapshot, onParticipation, onLocked, onLocation, onAccount }: {
  snapshot: Snapshot;
  onParticipation: () => void;
  onLocked: () => void;
  onLocation: () => void;
  onAccount: () => void;
}) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  return <main className="reading-shell">
    <header className="reading-header">
      <p className="eyebrow">已提交</p>
      <h1>{snapshot.title}</h1>
      <p>{snapshot.introduction}</p>
    </header>
    <section className="waiting-status" aria-labelledby="participation-entry-heading">
      <p className="eyebrow">浪漫匹配参与</p>
      <h2 id="participation-entry-heading">要开始匹配吗？</h2>
      <p>先填写展示资料、私密偏好和地点，再确认别人会看到的内容。提交问卷后，由你决定是否加入匹配。</p>
      <button className="button primary" onClick={onParticipation}>匹配设置</button>
    </section>
    <section className="snapshot-list">
      {snapshot.statements.map((statement, index) => <article className="snapshot-statement" key={`${statement.evidence.questionId}-${index}`}>
        <p className="statement-index">{String(index + 1).padStart(2, "0")}</p>
        <div>
          <h2>{statement.title}</h2>
          <p>{statement.body}</p>
          <p className="evidence-line">依据：{statement.evidence.questionId} · {statement.evidence.questionTitle} · 把握度 {Math.round(statement.confidence * 100)}%</p>
        </div>
      </article>)}
    </section>
    <p className="snapshot-caveat">{snapshot.caveat}</p>
    <div className="reading-actions">
      <button className="button secondary" onClick={onLocked}>查看已提交的回答</button>
      <button className="button secondary" onClick={onLocation}>地点设置</button>
      <button className="button secondary" onClick={onAccount}>账户与隐私</button>
    </div>
  </main>;
}

function LockedBoundaryAnswers({ unit, answers }: { unit: ClientUnit; answers: DraftDocument["answers"] }) {
  const { selected, custom, positions } = boundaryAnswers({ schemaVersion: "1.2.0", context: {}, answers, evidenceSources: {} });
  const concernField = unit.fields.find((field) => field.id === "F01.high_impact_behaviors");
  const options = concernField?.options ?? [];
  const rows = [...options.filter(({ id }) => selected.includes(id)).map(({ id, label }) => ({ id, text: label })), ...custom];
  return <><h3 className="question-prompt">{concernField?.prompt}</h3>{rows.length > 0 && <dl className="boundary-record">{rows.map((row) => <div key={row.id}>
    <dt>{row.text}</dt><dd className={!positions[row.id] || positions[row.id].state === "unanswered" ? "r4-answer-skipped" : undefined}>{positions[row.id] ? BOUNDARY_LABELS[positions[row.id].state] : "未回答"}</dd>
  </div>)}</dl>}</>;
}

function LockedView({ definition, submission, onBack, backLabel }: {
  definition: QuestionnaireDefinition;
  submission: {
    questionnaireVersion: string;
    context: Record<string, string>;
    answers: DraftDocument["answers"];
    evidenceSources: Record<string, string>;
    otherTexts?: Record<string, string>;
    migration?: DraftDocument["migration"];
    answersHash: string;
    submittedAt: string;
  };
  onBack: () => void;
  /** Only when no surrounding frame already provides Back. */
  backLabel?: string;
}) {
  const reader = useRef<HTMLElement>(null);
  const sectionControl = useRef<HTMLElement>(null);
  const [activeSection, setActiveSection] = useState(definition.sections[0]?.id);
  const units = [...definition.units, ...definition.conditionalUnits];
  const optionLabel = (options: Array<{ id: string; label: string }> | undefined, id: string) => options?.find((option) => option.id === id)?.label ?? id;
  const answered = units.filter((unit) => Object.keys(submission.answers[unit.id] ?? {}).length > 0
    || (unit.id === "F01" && Object.keys(submission.migration?.legacyF01 ?? {}).length > 0)
    || (unit.id === "F06" && Object.keys(submission.migration?.legacyF06 ?? {}).length > 0));
  const sections = definition.sections.map((section) => ({ ...section, units: answered.filter((unit) => unit.sectionId === section.id) })).filter((section) => section.units.length > 0);
  const sectionIds = sections.map(({ id }) => id).join(",");
  useEffect(() => {
    const content = reader.current;
    const control = sectionControl.current;
    const scroller = content?.closest<HTMLElement>(".r4-body");
    if (!content || !control || !scroller) return;
    const targets = [...content.querySelectorAll<HTMLElement>(".r4-answer-section")];
    if (!targets.length) return;
    let observer: IntersectionObserver | undefined;
    let atEnd = false;
    const syncSection = () => {
      // Read just beneath the pinned control. Tall sections remain current until the next one arrives.
      const line = scroller.getBoundingClientRect().top + control.offsetHeight + 12;
      const current = atEnd ? targets.at(-1) : targets.find((target) => {
        const bounds = target.getBoundingClientRect();
        return bounds.top <= line + 1 && bounds.bottom > line;
      });
      if (current?.dataset.section) setActiveSection(current.dataset.section);
      else if (scroller.scrollTop === 0 && targets[0].dataset.section) setActiveSection(targets[0].dataset.section);
    };
    const observe = () => {
      observer?.disconnect();
      const top = Math.min(control.offsetHeight + 12, scroller.clientHeight - 1);
      const bottom = Math.max(0, scroller.clientHeight - top - 1);
      content.style.setProperty("--answers-control-height", `${control.offsetHeight}px`);
      observer = new IntersectionObserver(syncSection, {
        root: scroller, rootMargin: `-${top}px 0px -${bottom}px 0px`, threshold: 0,
      });
      targets.forEach((target) => observer!.observe(target));
      syncSection();
    };
    // A short final section may never reach the reading line. At the actual scroll end it is current.
    const syncEnd = () => {
      const next = scroller.scrollTop > 0 && scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= 1;
      if (next !== atEnd) { atEnd = next; syncSection(); }
    };
    const resize = new ResizeObserver(observe);
    resize.observe(scroller);
    resize.observe(control);
    scroller.addEventListener("scroll", syncEnd, { passive: true });
    observe();
    return () => { observer?.disconnect(); resize.disconnect(); scroller.removeEventListener("scroll", syncEnd); };
  }, [sectionIds]);
  const goToSection = (id: string) => {
    const content = reader.current;
    const scroller = content?.closest<HTMLElement>(".r4-body");
    const target = content?.querySelector<HTMLElement>(`#answers-${id}`);
    if (!scroller || !target) return;
    const offset = (sectionControl.current?.offsetHeight ?? 0) + 12;
    scroller.scrollTo({
      top: scroller.scrollTop + target.getBoundingClientRect().top - scroller.getBoundingClientRect().top - offset,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
    setActiveSection(id);
    target.querySelector<HTMLElement>("h2")?.focus({ preventScroll: true });
  };
  const hasAnswer = (answer: AnswerValue | undefined) => answer !== undefined && (Array.isArray(answer) ? answer.length > 0 : String(answer).trim().length > 0);
  const boundary = boundaryAnswers({ schemaVersion: "1.2.0", context: {}, answers: submission.answers, evidenceSources: {} });
  /** The person's own answer is the reading text; the question is its quiet lead line. */
  const renderAnswer = (field: ClientUnit["fields"][number], answer: AnswerValue) => {
    if (!hasAnswer(answer)) return <p className="r4-answer-skipped">暂不回答</p>;
    if (Array.isArray(answer)) return <p className="r4-answer-text">{answer.map((id) => optionLabel(field.options, id)).join("、")}</p>;
    if (typeof answer === "number" && field.type === "scale" && field.min !== undefined && field.max !== undefined) {
      // A bare 1 or 5 means nothing on its own; show where the answer sits between the two ends (Founder 2026-10-07).
      const steps = Array.from({ length: field.max - field.min + 1 }, (_, i) => field.min! + i);
      return <div className="r4-answer-scale" role="img" aria-label={`${answer}（${field.min} · ${field.leftLabel}，${field.max} · ${field.rightLabel}）`}>
        <span className="r4-scale-dots" aria-hidden="true">{steps.map((v) => <i key={v} className={v === answer ? "on" : undefined} />)}</span>
        <span className="r4-scale-ends" aria-hidden="true"><span>{field.leftLabel}</span><span>{field.rightLabel}</span></span>
      </div>;
    }
    const text = typeof answer === "string" && field.options ? optionLabel(field.options, answer) : String(answer);
    return <p className={text === "暂不回答" ? "r4-answer-skipped" : "r4-answer-text"}>{text}</p>;
  };
  return <main className="reading-shell locked-view r4-locked" ref={reader}>
    <nav className="r4-section-jumps" ref={sectionControl} aria-label="Sections">{sections.map((section) => <a key={section.id} href={`#answers-${section.id}`} aria-current={activeSection === section.id ? "location" : undefined} onClick={(event) => { event.preventDefault(); goToSection(section.id); }}>{section.id}</a>)}</nav>
    <header className="r4-locked-summary">
      <h1 className="visually-hidden">已提交的回答</h1>
      <p className="r4-locked-meta">已提交，不可修改 · <time dateTime={submission.submittedAt} title={new Date(submission.submittedAt).toLocaleString("zh-CN", { dateStyle: "medium", timeStyle: "short" })}>{new Date(submission.submittedAt).toLocaleDateString("zh-CN", { dateStyle: "medium" })}</time> · 版本 {submission.questionnaireVersion}</p>
    </header>
    <section className="locked-section" hidden={definition.context.fields.length === 0}>
      <h2>填写背景</h2>
      {definition.context.fields.map((field) => <div className="locked-answer" key={field.id}>
        <h3>{field.prompt}</h3>
        <p className="r4-answer-text">{optionLabel(field.options, submission.context[field.id])}</p>
      </div>)}
    </section>
    {sections.map((section) => <section className="r4-answer-section" id={`answers-${section.id}`} key={section.id} data-section={section.id} aria-labelledby={`answers-${section.id}-heading`}>
      <h2 id={`answers-${section.id}-heading`} tabIndex={-1}>{section.id} · {section.units.length} 题</h2>
      <ol className="r4-group r4-answer-panel" aria-label={`${section.id} · ${section.units.length} 题`}>
        {section.units.map((unit, index) => {
          const currentFields = unit.fields.filter((field) => submission.answers[unit.id]?.[field.id] !== undefined);
          const optionalUnanswered = unit.id === "F01" && definition.version === "1.2.0"
            ? boundary.selected.length === 0 && boundary.custom.length === 0
            : unit.id === "F06" && !currentFields.some((field) => hasAnswer(scalarAnswer(submission.answers[unit.id][field.id])));
          return <li className="r4-answer-unit" key={unit.id}>
          <span className="r4-answer-number" aria-hidden="true">{section.id}{index + 1}</span>
          <div className="r4-answer-body">
          {unit.id === "F06" && optionalUnanswered && currentFields.length === 0 && <h3 className="question-prompt">{unit.fields[0]?.prompt}</h3>}
          {unit.id === "F01" && definition.version === "1.2.0" ? <LockedBoundaryAnswers unit={unit} answers={submission.answers} /> : currentFields.map((field, fieldIndex) => {
            const answer = scalarAnswer(submission.answers[unit.id][field.id]);
            if (answer === undefined) return null;
            return <div className="locked-answer" key={field.id}>
              <h3>{fieldIndex > 0 && <span className="r4-answer-subnumber">{section.id}{index + 1}.{fieldIndex}</span>}{field.prompt}</h3>{!(optionalUnanswered && unit.id === "F06") && renderAnswer(field, answer)}
              {submission.otherTexts?.[field.id] && <p className="r4-answer-text r4-answer-other">{submission.otherTexts[field.id]}</p>}
            </div>;
          })}
          {submission.evidenceSources[unit.id] && <p className="evidence-line">回答依据：{optionLabel(definition.evidenceSourceOptions, submission.evidenceSources[unit.id])}</p>}
          {(unit.id === "F01" || unit.id === "F06") && <PreviousQuestionnaireAnswers document={submission} unit={unit} scope={unit.id} reading unanswered={optionalUnanswered} />}
          </div>
        </li>; })}
      </ol>
    </section>)}
    <details className="hash-line"><summary>记录校验信息</summary><p>{submission.answersHash}</p></details>
    {backLabel && <button className="button secondary" onClick={onBack}>{backLabel}</button>}
  </main>;
}

function AccountPrivacyView({ accountId, hasSubmission, onBack, onLogout, onDeleted }: {
  accountId: string;
  hasSubmission: boolean;
  onBack: () => void;
  onLogout: () => void;
  onDeleted: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [confirmationId, setConfirmationId] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Connection Messages v1: with any Connection, the person chooses what counterparts keep; nothing is preselected.
  const [hasConversations, setHasConversations] = useState(false);
  const [conversationChoice, setConversationChoice] = useState<"keep" | "erase" | null>(null);
  useEffect(() => {
    void api<{ conversations: unknown[] }>("/api/v1/pairing/conversations")
      .then((result) => setHasConversations(result.conversations.length > 0))
      .catch(() => undefined);
  }, []);
  const confirmed = confirmationId.trim().toUpperCase() === accountId && password.length > 0
    && (!hasConversations || conversationChoice !== null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const closeDialog = () => {
    if (busy) return;
    dialogRef.current?.close();
    setConfirmationId("");
    setPassword("");
    setConversationChoice(null);
    setError("");
  };

  const removeAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!confirmed) return;
    setBusy(true);
    setError("");
    try {
      await api<{ deleted: true }>("/api/v1/account", {
        method: "DELETE",
        body: JSON.stringify({ accountId: confirmationId, password, ...(conversationChoice ? { conversationChoice } : {}) }),
      });
      onDeleted();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "conversation_choice_required") setHasConversations(true);
      setError(caught instanceof Error ? caught.message : "账户未能删除");
    } finally {
      setBusy(false);
    }
  };

  return <div className="settings-layout">
    <header className="app-header">
      <button className="wordmark" onClick={onBack}>Perhaps</button>
      <span className="save-status">账户与隐私</span>
      <button className="text-button" onClick={onLogout}>退出</button>
    </header>
    <main className="settings-main account-main r4-account">
      <section className="r4-account-id" aria-labelledby="account-id-heading">
        <h2 id="account-id-heading">账户编号</h2>
        <p className="r4-account-number">{accountId}</p>
        <p className={`r4-state${hasSubmission ? "" : " quiet"}`}>{hasSubmission ? "问卷已提交" : "问卷尚未提交"}</p>
        <p className="r4-account-note">{hasSubmission
          ? "加入匹配后，如果有可以继续了解的人，你会在这里看到对方的展示资料，再决定是否愿意认识。"
          : "草稿会自动保存到你的匿名账户。换一台设备，用同一个账户编号和密码登录，就能接着填写。"}</p>
      </section>

      <section className="r4-account-section" aria-labelledby="data-boundary-heading">
        <h2 id="data-boundary-heading">当前如何使用你的数据</h2>
        <dl className="r4-group r4-facts">
          <div><span className="r4-row-icon" aria-hidden="true"><AppIcon name="questionnaire" /></span><dt>问卷回答</dt><dd>登录后可以查看自己的草稿和已提交回答。其他参与者无法查看。</dd></div>
          <div><span className="r4-row-icon" aria-hidden="true"><AppIcon name="participation" /></span><dt>用于匹配的信息</dt><dd>选择题按固定规则用于匹配。内部评估保存在服务器，不对参与者展示。</dd></div>
          <div><span className="r4-row-icon" aria-hidden="true"><AppIcon name="note" /></span><dt>开放题</dt><dd>只保存原文，当前阶段不调用 AI，也不会把原文公开给其他参与者。</dd></div>
          <div><span className="r4-row-icon" aria-hidden="true"><AppIcon name="location" /></span><dt>地点设置</dt><dd>地点单独保存，只用于筛选范围，不改动已提交的回答或匹配评分。</dd></div>
          <div><span className="r4-row-icon" aria-hidden="true"><AppIcon name="connections" /></span><dt>对话</dt><dd>只有你和已建立连接的对方能看到。内容加密保存，运营方无法查看；只有在有人举报时，被附上的那几条消息会交给运营方处理，处理结束后删除。</dd></div>
          <div><span className="r4-row-icon" aria-hidden="true"><AppIcon name="lock" /></span><dt>身份与联系</dt><dd>登录不需要姓名或邮箱，使用账户编号和密码；恢复密钥用于找回账户。联系方式可以不填。双方都愿意认识后，对方才能看到你当前选择分享的联系方式。</dd></div>
        </dl>
      </section>

      <section className="r4-account-section r4-danger" aria-labelledby="delete-account-heading">
        <h2 id="delete-account-heading">删除账户</h2>
        <p>这会永久删除账户、登录与恢复信息、问卷回答、匹配数据、地点设置和保存的联系方式，并让所有设备退出登录。如果你有已建立的连接，删除前可以选择把对话留给对方，或者抹去你说过的话。</p>
        <p>删除无法撤销。已经消耗的邀请码次数不会退回，但问卷内容不会因此保留。</p>
        <button className="button destructive" onClick={() => dialogRef.current?.showModal()}>永久删除我的账户</button>
      </section>
    </main>

    <dialog className="account-dialog" ref={dialogRef} onCancel={(event) => {
      if (busy) event.preventDefault();
      else closeDialog();
    }}>
      <form onSubmit={removeAccount}>
        <p className="eyebrow danger-text">无法撤销</p>
        <h2>确认永久删除</h2>
        <p className="dialog-copy">请输入当前账户编号和密码。确认后，账户及其关联数据会立即删除。</p>
        <label className="dialog-field">
          <span>账户编号</span>
          <input value={confirmationId} onChange={(event) => setConfirmationId(event.target.value)} autoComplete="username" required />
          <small>请输入：{accountId}</small>
        </label>
        <label className="dialog-field">
          <span>当前密码</span>
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
        </label>
        {hasConversations && <fieldset className="r4-report-field r4-departure">
          <legend>你们的对话怎么处理？</legend>
          <label className="r4-option"><input type="radio" name="conversation-choice" checked={conversationChoice === "keep"} onChange={() => setConversationChoice("keep")} />
            <span><strong>保留给对方</strong>对方仍能看到完整的对话，你的名字会显示为已离开 Perhaps。</span></label>
          <label className="r4-option"><input type="radio" name="conversation-choice" checked={conversationChoice === "erase"} onChange={() => setConversationChoice("erase")} />
            <span><strong>抹去我说过的话</strong>你发出的每一条消息和你的名字都会被删除，对方只会看到空白的位置。</span></label>
          <small>已被举报的消息会保留到处理结束，最长 90 天。</small>
        </fieldset>}
        {error && <p className="error-message" role="alert">{error}</p>}
        <div className="dialog-actions">
          <button className="button secondary" type="button" disabled={busy} onClick={closeDialog}>取消</button>
          <button className="button destructive" disabled={!confirmed || busy}>{busy ? "Deleting" : "永久删除"}</button>
        </div>
      </form>
    </dialog>
  </div>;
}

function QuestionnaireFlow({ definition, initial, onSubmitted, onLocation, onAccount, onClose }: {
  definition: QuestionnaireDefinition;
  initial: { document: DraftDocument; revision: number; currentUnitId: string | null };
  onClose: (saved: { document: DraftDocument; revision: number; currentUnitId: string | null }) => void;
  onSubmitted: (result: { submissionId: string; submittedAt: string; answersHash: string }) => void;
  onLocation: () => void;
  onAccount: () => void;
}) {
  const [leaving, setLeaving] = useState(false);
  const [indexOpen, setIndexOpen] = useState(false);
  const [document, setDocument] = useState(initial.document);
  const [view, setView] = useState<View>("questionnaire");
  const visibleUnits = useMemo(() => [
    ...definition.units,
    ...definition.conditionalUnits.filter((unit) => {
      const rule = definition.conditionalRules[unit.id];
      return rule ? rule.values.includes(document.context[rule.fieldId]) : false;
    }),
  ], [definition, document.context]);
  const firstStep = definition.context.fields.length > 0 ? 0 : 1;
  const initialIndex = Math.max(firstStep, visibleUnits.findIndex((unit) => unit.id === initial.currentUnitId) + 1);
  const [step, setStep] = useState(initialIndex);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [submitError, setSubmitError] = useState("");
  const [confirmations, setConfirmations] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const currentUnit = step === 0 ? null : visibleUnits[Math.min(step - 1, visibleUnits.length - 1)];
  const autosave = useAutosave(document, currentUnit?.id ?? "context", initial.revision, true);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {if(autosave.hasUnsaved){event.preventDefault();event.returnValue="";}};
    window.addEventListener("beforeunload",warn);return ()=>window.removeEventListener("beforeunload",warn);
  }, [autosave.hasUnsaved]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [step, view]);

  const updateAnswer = (unitId: string, fieldId: string, value: AnswerValue) => {
    setDocument((current) => ({
      ...current,
      answers: { ...current.answers, [unitId]: { ...(current.answers[unitId] ?? {}), [fieldId]: value } },
    }));
    setIssues((current) => current.filter((issue) => issue.path !== `answers.${unitId}.${fieldId}`));
  };

  const leaveQuestionnaire = async (destination: () => void) => {
    setSubmitError("");
    try {
      await autosave.persist();
      destination();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "草稿未能保存，暂时无法离开");
    }
  };

  const close = async () => {
    setLeaving(true); setSubmitError("");
    try { const revision = await autosave.persist(); onClose({document,revision,currentUnitId:currentUnit?.id ?? "context"}); }
    catch(error) { setSubmitError(error instanceof Error ? error.message : "草稿没有保存成功，请再试一次，然后关闭。"); }
    finally {setLeaving(false);}
  };

  const review = async () => {
    setSubmitError("");
    try {
      await autosave.persist();
      const result = await api<{ complete: boolean; revision: number; issues: ValidationIssue[] }>(`/api/v1/questionnaire/check?version=${document.schemaVersion}`, { method: "POST" });
      setIssues(result.issues);
      setView("review");
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "无法检查问卷");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const revision = await autosave.persist();
      const result = await api<{ submissionId: string; submittedAt: string; answersHash: string }>(`/api/v1/questionnaire/submit?version=${document.schemaVersion}`, {
        method: "POST",
        body: JSON.stringify({ revision, confirmations }),
      });
      onSubmitted(result);
    } catch (error) {
      if (error instanceof ApiError && error.details && typeof error.details === "object" && "issues" in error.details) {
        setIssues((error.details as { issues: ValidationIssue[] }).issues);
      }
      setSubmitError(error instanceof Error ? error.message : "提交未能完成");
    } finally {
      setSubmitting(false);
    }
  };

  if (view === "review") {
    return <ParticipantFrame title="Review Answers" screenKey="questionnaire-review" onBack={()=>setView("questionnaire")}><main className="reading-shell review-shell">
      <header className="reading-header">
        <h1>{issues.length === 0 ? "确认提交" : `还有 ${issues.length} 处需要确认`}</h1>
        <p>提交后，问卷回答将无法修改。</p>
      </header>
      {issues.length > 0 && <section className="issue-list" aria-label="未完成内容">
        {issues.map((issue) => <button key={`${issue.path}-${issue.code}`} onClick={() => {
          const unitId = issue.path === "f01Reviewed" ? "F01" : issue.path.split(".")[1];
          const index = visibleUnits.findIndex((unit) => unit.id === unitId);
          setStep(issue.path.startsWith("context.") ? 0 : Math.max(0, index + 1));
          setView("questionnaire");
        }}><span>{visibleUnits.find((unit) => issue.path.includes(unit.id))?.fields[0]?.prompt ?? "问卷回答"}</span><strong>{apiErrorCopy(issue.message)}</strong></button>)}
      </section>}
      {issues.length === 0 && <section className="confirmation-list">
        {definition.submissionConfirmations.map((confirmation) => <label className="confirmation-row" key={confirmation}>
          <input type="checkbox" checked={confirmations.includes(confirmation)} onChange={(event) => setConfirmations((current) => event.target.checked ? [...current, confirmation] : current.filter((value) => value !== confirmation))} />
          <span>{submissionConfirmationLabel(confirmation)}</span>
        </label>)}
      </section>}
      {submitError && <p className="error-message" role="alert">{submitError}</p>}
      <div className="review-actions">
        <button className="button secondary" onClick={() => setView("questionnaire")}>返回修改</button>
        {issues.length === 0 && <button className="button primary" disabled={confirmations.length !== definition.submissionConfirmations.length || submitting} onClick={submit}>{submitting ? "Submitting" : "提交并继续"}</button>}
      </div>
    </main></ParticipantFrame>;
  }

  const groupId = currentUnit?.sectionId;
  const nextAction = <button className="button primary" disabled={leaving} onClick={() => {
    if (step < visibleUnits.length) {
      if (currentUnit?.id === "F01" && document.schemaVersion === "1.2.0") setDocument(current=>({...current,f01Reviewed:true}));
      setStep(current=>current+1);
    } else void review();
  }}>{step === visibleUnits.length ? "Review answers" : currentUnit?.id === "F01" && document.f01Reviewed === false ? "确认并继续" : "Continue"}</button>;
  /** Same codes as Submitted Answers: section letter + position in that section (A1, A2 …). */
  const unitCode = (unit: { id: string; sectionId?: string }) => `${unit.sectionId ?? ""}${visibleUnits.filter(u => u.sectionId === unit.sectionId).findIndex(u => u.id === unit.id) + 1}`;
  return <ParticipantFrame title={indexOpen ? "Questionnaire" : step ? `${step} / ${visibleUnits.length}` : "Questionnaire"} screenKey={`questionnaire-${step}-${indexOpen}`}
    onBack={()=>indexOpen ? setIndexOpen(false) : step>firstStep ? setStep(step-1) : void close()}
    actions={<><button className="r4-icon" aria-label="Questionnaire" disabled={leaving} onClick={()=>setIndexOpen(value=>!value)}><AppIcon name="questionnaire" /></button><button disabled={leaving} className="text-button" onClick={()=>void close()}>{leaving ? "Saving" : "Close"}</button></>}
    dock={!indexOpen && nextAction}>
    <main className="questionnaire-main">
      {indexOpen ? <div className="r4-question-index">{definition.sections.map(group=><section key={group.id}><h2>{group.id} · {visibleUnits.filter(unit=>unit.sectionId===group.id).length} 题</h2>{visibleUnits.map((unit,index)=>unit.sectionId===group.id?<button key={unit.id} className="r4-menu-row" onClick={()=>{setStep(index+1);setIndexOpen(false);}}><span><small>{unitCode(unit)}</small>{unit.fields[0]?.prompt}</span></button>:null)}</section>)}</div> : <>
        <aside className="step-context">{groupId && <p>{currentUnit ? `${unitCode(currentUnit)} · ` : ""}{groupId} · {visibleUnits.filter(unit=>unit.sectionId===groupId).length} 题</p>}<span className={`save-status ${autosave.status}`} role="status">{autosave.message}</span></aside>
        <div className="progress-track" role="progressbar" aria-label="问卷进度" aria-valuemin={0} aria-valuemax={visibleUnits.length} aria-valuenow={step}><span style={{width:`${Math.round(step/visibleUnits.length*100)}%`}} /></div>
        <fieldset className="r4-question-content" disabled={leaving || submitting}>
          {step===0 ? <ContextStep definition={definition} document={document} onChange={(fieldId,value)=>setDocument(current=>({...current,context:{...current.context,[fieldId]:value}}))} />
          : currentUnit?.id === "F01" && document.schemaVersion === "1.2.0" ? <QuestionnaireBoundaryStep unit={currentUnit} document={document} issues={issues} onChange={next=>{setDocument(next);setIssues(current=>current.filter(({path})=>!path.startsWith("answers.F01")&&path!=="f01Reviewed"));}} />
          : currentUnit && <UnitStep definition={definition} unit={currentUnit} document={document} issues={issues}
            onAnswer={(fieldId,value)=>updateAnswer(currentUnit.id,fieldId,value)}
            onOtherText={(fieldId,value)=>setDocument(current=>({...current,otherTexts:{...current.otherTexts,[fieldId]:value}}))}
            onEvidence={value=>{setDocument(current=>({...current,evidenceSources:{...current.evidenceSources,[currentUnit.id]:value}}));setIssues(current=>current.filter(issue=>issue.path!==`evidenceSources.${currentUnit.id}`));}} />}
        </fieldset>
      </>}
      {submitError && <p className="error-message" role="alert">{submitError}</p>}
    </main>
  </ParticipantFrame>;
}

export function hasQuestionnaireProgress(draft: {revision:number;currentUnitId:string|null} | null): boolean {
  return Boolean(draft && (draft.revision > 0 || draft.currentUnitId !== null));
}

/** Add to Home Screen: shows the icon the person will get, then the three Safari steps; says so when already installed. */
function InstallGuide() {
  const installed = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);
  return <main className="r4-install">
    <div className="r4-install-icon"><img src="/app-icon.svg" alt="" width="84" height="84" /><span>Perhaps</span></div>
    {installed ? <><h2>Perhaps 已在主屏幕上</h2><p>之后可以直接从主屏幕打开。</p></> : <><h2>添加 Perhaps</h2>
      <ol className="r4-steps">
        <li><span className="r4-step-mark">1</span><span>在 iPhone 上用 Safari 打开 Perhaps</span></li>
        <li><span className="r4-step-mark"><AppIcon name="share" /></span><span>轻点分享按钮</span></li>
        <li><span className="r4-step-mark"><AppIcon name="home" /></span><span>选择“添加到主屏幕”</span></li>
      </ol></>}
    <p className="r4-install-note">用同一个账户登录就能继续。关闭页面不会丢失已保存的问卷和参与状态。</p>
  </main>;
}

function AppContent() {
  const spaces = useSpaces();
  const [sessionNotice, setSessionNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [definition, setDefinition] = useState<QuestionnaireDefinition | null>(null);
  const [draft, setDraft] = useState<{ document: DraftDocument; revision: number; currentUnitId: string | null } | null>(null);
  const [submission, setSubmission] = useState<Parameters<typeof LockedView>[0]["submission"] | null>(null);
  const [view, setView] = useState<"home" | "welcome" | "intro" | "questionnaire" | "locked" | "location" | "account" | "install">("home");
  // A tapped message notification opens the app on Connections (public/sw.js).
  const [homeScreen, setHomeScreen] = useState<HomeScreen>(() => {
    if (new URLSearchParams(window.location.search).get("screen") !== "connections") return "explore";
    // Keep the notification space until the authenticated space provider reads it.
    return "connections";
  });
  useEffect(() => { spaces.setAccount(session?.accountId ?? null); }, [session?.accountId]);
  const workspaceEpoch = useRef(0);
  const sessionChannel = useRef<BroadcastChannel | null>(null);
  const [fatalError, setFatalError] = useState("");
  const [accountDeleted, setAccountDeleted] = useState(false);

  const loadWorkspace = useCallback(async () => {
    const epoch = ++workspaceEpoch.current;
    const loadLocked = async () => {
      const result = await api<Parameters<typeof LockedView>[0]["submission"]>(`/api/v1/questionnaire/submission?version=${CURRENT_QUESTIONNAIRE_VERSION}`);
      const source = await api<QuestionnaireDefinition>(`/api/v1/questionnaire/definition?version=${result.questionnaireVersion}`);
      if (source.version !== result.questionnaireVersion) throw new Error("问卷记录已更新，请刷新后查看。");
      if (epoch !== workspaceEpoch.current) return;
      setDefinition(applyQuestionnaireCopy(source)); setSubmission(result); setDraft(null); setView("home");
    };
    // The current questionnaire and, for people who have submitted, their record are read alongside the draft;
    // a different version falls through to its own read below.
    prefetch([`/api/v1/questionnaire/definition?version=${CURRENT_QUESTIONNAIRE_VERSION}`, `/api/v1/questionnaire/submission?version=${CURRENT_QUESTIONNAIRE_VERSION}`]);
    const result = await api<DraftPayload>(`/api/v1/questionnaire/draft?version=${CURRENT_QUESTIONNAIRE_VERSION}`);
    if (result.status === "submitted") { await loadLocked(); return; }
    const document = result.document ?? EMPTY_DRAFT;
    const source = await api<QuestionnaireDefinition>(`/api/v1/questionnaire/definition?version=${document.schemaVersion}`);
    if (source.version !== document.schemaVersion) { await loadLocked(); return; }
    if (epoch !== workspaceEpoch.current) return;
    setDefinition(applyQuestionnaireCopy(source)); setSubmission(null);
    setDraft({ document, revision: result.revision ?? 0, currentUnitId: result.currentUnitId ?? null });
    setView("home");
  }, []);

  useEffect(() => {
    void api<SessionProbe>("/api/v1/session").then(async (result) => {
      if (!result.authenticated || !result.accountId || !result.csrfToken || !result.expiresAt) return;
      const next = {accountId:result.accountId,csrfToken:result.csrfToken,expiresAt:result.expiresAt};
      setCsrfToken(next.csrfToken); setSession(next); prefetch(waitingPoolReads()); await loadWorkspace();
    }).catch(error => {if (!(error instanceof ApiError) || error.status !== 401) setFatalError(error instanceof Error ? error.message : "应用无法启动");}).finally(() => setLoading(false));
  }, [loadWorkspace]);

  const authenticated = async (result: SessionPayload, created = false) => {
    sessionChannel.current?.postMessage("changed");
    setSessionNotice(""); setSession(result); setLoading(true); setFatalError(""); setHomeScreen("explore"); prefetch(waitingPoolReads());
    try { await loadWorkspace(); if (created) setView("welcome"); }
    catch(error) {setFatalError(error instanceof Error ? error.message : "无法读取账户");}
    finally {setLoading(false);}
  };
  const clearSession = () => {
    workspaceEpoch.current++;
    clearScrollMemory(); clearPrefetch();
    setCsrfToken(null);setSession(null);setDefinition(null);setDraft(null);setSubmission(null);setView("home");setHomeScreen("explore");setFatalError("");
  };
  useEffect(() => {
    if(typeof BroadcastChannel === "undefined")return;
    const channel=new BroadcastChannel("perhaps-session");sessionChannel.current=channel;
    channel.onmessage=event=>{if(event.data==="changed"){clearSession();setSessionNotice("登录状态已变化，请重新登录。");}};
    return ()=>{channel.close();sessionChannel.current=null;};
  }, []);
  useEffect(() => {
    const expired = () => {clearSession();setSessionNotice("登录状态已失效，请重新登录。已保存的内容会保留。");};
    window.addEventListener("perhaps:session-expired",expired);
    return ()=>window.removeEventListener("perhaps:session-expired",expired);
  }, []);
  const logout = async () => {try {await disablePushForThisDevice().catch(() => undefined);await api("/api/v1/session",{method:"DELETE"});} catch { /* Local state must still be cleared. */ } finally {sessionChannel.current?.postMessage("changed");setSessionNotice("");clearSession();}};
  const home = () => {setHomeScreen("me");setView("home");};
  // Location opens from Explore, Me, Participation or the questionnaire; Back returns to that exact place.
  const [locationReturn, setLocationReturn] = useState<{ view: "home" | "questionnaire"; screen: HomeScreen }>({ view: "home", screen: "me" });
  const [locationFocus, setLocationFocus] = useState<"preferred" | null>(null);
  const openLocation = (from: "home" | "questionnaire", focus: "preferred" | null = null) => {setLocationReturn({ view: from, screen: homeScreen });setLocationFocus(focus);setView("location");};
  const leaveLocation = () => {setHomeScreen(locationReturn.screen);setView(locationReturn.view);};
  const openQuestionnaire = () => setView(submission ? "locked" : hasQuestionnaireProgress(draft) ? "questionnaire" : "intro");

  if (loading) return <ParticipantFrame screenKey="loading" balanced><main><LoadingState /></main></ParticipantFrame>;
  if (fatalError) return <ParticipantFrame screenKey="error" balanced><main><h2>暂时无法打开</h2><p role="alert">{fatalError}</p><button className="button" onClick={()=>window.location.reload()}>Reload</button></main></ParticipantFrame>;
  if (accountDeleted) return <ParticipantFrame screenKey="deleted" balanced><main><h2>账户已永久删除</h2><p>所有设备都已退出登录。以后想重新参与，需要新的邀请码。</p><button className="button" onClick={()=>setAccountDeleted(false)}>回到 Perhaps</button></main></ParticipantFrame>;
  if (!session) return <AuthScreen notice={sessionNotice} onAuthenticated={(result,created)=>void authenticated(result,created)} />;
  if (!definition) return <ParticipantFrame screenKey="loading" balanced><main><LoadingState /></main></ParticipantFrame>;
  if (view === "welcome") return <ParticipantFrame screenKey="account-ready" balanced><main><h1>欢迎来到 Perhaps</h1><p className="r4-muted">问卷可以之后再填。完成并提交后，再决定是否加入 Matching Pool。</p><div className="r4-choice-actions"><button className="button" onClick={()=>setView("home")}>先看看</button><button className="button" onClick={()=>setView("intro")}>填写问卷</button></div></main></ParticipantFrame>;
  if (view === "intro") return <ParticipantFrame title="Questionnaire" screenKey="questionnaire-intro" onBack={home} balanced><main><p>{definition.privacyPromise}</p><p>提交前可以修改，提交后将保留为不可修改的记录。</p><div className="r4-choice-actions"><button className="button" onClick={()=>setView("questionnaire")}>开始填写</button><button className="button" onClick={home}>稍后再填</button></div></main></ParticipantFrame>;
  if (view === "questionnaire" && draft) return <QuestionnaireFlow definition={definition} initial={draft}
    onClose={(saved)=>{setDraft(saved);home();}}
    onLocation={()=>openLocation("questionnaire")} onAccount={()=>setView("account")}
    onSubmitted={()=>{setLoading(true);setHomeScreen("participation");void loadWorkspace().catch(error=>setFatalError(error.message)).finally(()=>setLoading(false));}} />;
  if (view === "locked" && submission) return <ParticipantFrame title="Submitted Answers" screenKey="locked" onBack={home}><LockedView definition={definition} submission={submission} onBack={home} /></ParticipantFrame>;
  if (view === "location") return <ParticipantFrame title="Location" screenKey="location" onBack={leaveLocation}><LocationSettings onBack={leaveLocation} onLogout={()=>void logout()} focus={locationFocus} /></ParticipantFrame>;
  if (view === "account") return <ParticipantFrame title="Account & Privacy" screenKey="account" onBack={home}><AccountPrivacyView accountId={session.accountId} hasSubmission={!!submission} onBack={home} onLogout={()=>void logout()} onDeleted={()=>{sessionChannel.current?.postMessage("changed");clearSession();setAccountDeleted(true);}} /></ParticipantFrame>;
  if (view === "install") return <ParticipantFrame title="Home Screen" screenKey="install" onBack={home} balanced><InstallGuide /></ParticipantFrame>;
  return <WaitingPoolView key={session.accountId} initialScreen={homeScreen} hasSubmission={!!submission} hasQuestionnaireDraft={hasQuestionnaireProgress(draft)}
    onScreenChange={setHomeScreen} onQuestionnaire={openQuestionnaire} onInstall={()=>setView("install")}
    onBack={home} onLocation={(focus)=>openLocation("home", focus ?? null)} onAccount={()=>setView("account")} onLogout={()=>void logout()} />;
}

export function App() {
  useEffect(() => {
    const focusTitle = (event: Event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.open || !dialog.closest(".participant-app")) return;
      const title = dialog.querySelector<HTMLElement>("h2");
      if (title) {title.tabIndex=-1;title.focus({preventScroll:true});}
    };
    document.addEventListener("toggle",focusTitle,true);
    return ()=>document.removeEventListener("toggle",focusTitle,true);
  }, []);
  return <div className="participant-app"><SpaceProvider><AppContent /></SpaceProvider></div>;
}
